import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CloseIcon from "@mui/icons-material/Close";
import { formatDistanceToNow } from "date-fns";
import DgModal from "./DgModal";
import { enUS } from "date-fns/locale";

const RiskHistoryModal = ({ isOpen, onClose, riskHistory = [], strategyName }) => {
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/New_York'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getTimeAgo = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true, 
        locale: enUS 
      });
    } catch (error) {
      return '';
    }
  };

  const getRiskChange = (previousRisk, newRisk) => {
    const change = Number(newRisk) - Number(previousRisk);
    if (change > 0) {
      return {
        icon: <TrendingUpIcon sx={{ fontSize: '16px' }} />,
        text: `+${change.toFixed(1)}`,
        color: '#FF6B6B',
        bgColor: 'rgba(255, 107, 107, 0.1)'
      };
    } else if (change < 0) {
      return {
        icon: <TrendingDownIcon sx={{ fontSize: '16px' }} />,
        text: `${change.toFixed(1)}`,
        color: '#51CF66',
        bgColor: 'rgba(81, 207, 102, 0.1)'
      };
    } else {
      return {
        icon: <span style={{ fontSize: '16px' }}>−</span>,
        text: '0.0',
        color: '#95B2C5',
        bgColor: 'rgba(149, 178, 197, 0.1)'
      };
    }
  };

  // Sort by descending timestamp (most recent first)
  const sortedHistory = [...riskHistory].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  return (
    <DgModal modalState={isOpen} handleModalState={onClose}>
      <div style={{ 
        width: '100%', 
        maxWidth: '100%',
      
        overflow: 'hidden',
       
      }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            paddingRight: '50px'
          }}>
            <div style={{
        
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <HistoryIcon sx={{ color: '#ffffff', fontSize: '24px' }} />
            </div>
            <div>
              <h1 style={{ 
                margin: 0,
                color: '#ffffff',
                fontSize: '1.5rem',
                fontWeight: '600',
                letterSpacing: '-0.025em'
              }}>
                Risk Score History
              </h1>
              {strategyName && (
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  margin: '4px 0 0 0',
                  fontSize: '0.875rem'
                }}>
                  Strategy: {strategyName}
                </p>
              )}
            </div>
          </div>

        {/* Content area */}
        <div style={{ 
          padding: '24px 28px',
          maxHeight: '480px', 
          overflowY: 'auto',
        }}>
          {sortedHistory.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              color: '#95B2C5'
            }}>
              <div style={{
              
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <HistoryIcon sx={{ fontSize: '32px', color: '#95B2C5' }} />
              </div>
              <h3 style={{ 
                fontSize: '1.1rem',
                fontWeight: '600',
                margin: '0 0 8px 0',
                color: '#ffffff'
              }}>
                No history available
              </h3>
              <p style={{ 
                fontSize: '0.875rem',
                margin: 0,
                opacity: 0.7
              }}>
                Risk score changes will appear here
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {sortedHistory.map((change, index) => {
                const riskChangeInfo = getRiskChange(change.previousRisk, change.newRisk);
                const isLatest = index === 0;
                
                return (
                  <div 
                    key={index}
                    style={{
                      background: isLatest 
                        ? 'linear-gradient(135deg, rgba(51, 87, 255, 0.1) 0%, rgba(91, 115, 255, 0.05) 100%)'
                        : 'rgba(26, 35, 50, 0.6)',
                      border: isLatest 
                        ? '1px solid rgba(51, 87, 255, 0.3)' 
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '16px',
                      padding: '20px',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      {/* User info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          background: 'rgba(149, 178, 197, 0.15)',
                          borderRadius: '10px',
                          padding: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PersonIcon sx={{ color: '#95B2C5', fontSize: '18px' }} />
                        </div>
                        <div>
                          <div style={{ 
                            fontWeight: '600',
                            color: '#ffffff',
                            fontSize: '0.95rem',
                            marginBottom: '2px'
                          }}>
                            {change.userName || change.userEmail || "Manager"}
                          </div>
                          {isLatest && (
                            <span style={{
                              background: 'linear-gradient(90deg, #3357FF, #5B73FF)',
                              color: '#ffffff',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Latest
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Date info */}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '0.85rem', 
                          color: '#ffffff',
                          marginBottom: '2px',
                          fontWeight: '500'
                        }}>
                          {formatDate(change.timestamp)}
                        </div>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: '#95B2C5',
                          opacity: 0.8
                        }}>
                          {getTimeAgo(change.timestamp)}
                        </div>
                      </div>
                    </div>

                    {/* Risk score change */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'rgba(0, 0, 0, 0.2)',
                      padding: '16px 18px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.05)'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{
                          background: 'rgba(149, 178, 197, 0.1)',
                          borderRadius: '8px',
                          padding: '6px 12px',
                          border: '1px solid rgba(149, 178, 197, 0.2)'
                        }}>
                          <span style={{ 
                            color: '#95B2C5', 
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}>
                            {change.previousRisk != null ? Number(change.previousRisk).toFixed(1) : '0.0'}
                          </span>
                        </div>
                        
                        <div style={{
                          color: '#6B8196',
                          fontSize: '1.2rem',
                          fontWeight: '300'
                        }}>
                          →
                        </div>
                        
                        <div style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '6px 12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <span style={{ 
                            color: '#ffffff', 
                            fontWeight: '700',
                            fontSize: '1rem'
                          }}>
                            {change.newRisk != null ? Number(change.newRisk).toFixed(1) : '0.0'}
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: riskChangeInfo.bgColor,
                        padding: '6px 12px',
                        borderRadius: '20px',
                        border: `1px solid ${riskChangeInfo.color}33`
                      }}>
                        <span style={{ color: riskChangeInfo.color }}>
                          {riskChangeInfo.icon}
                        </span>
                        <span style={{ 
                          color: riskChangeInfo.color,
                          fontWeight: '700',
                          fontSize: '0.85rem'
                        }}>
                          {riskChangeInfo.text}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DgModal>
  );
};

export default RiskHistoryModal;